import React from 'react';
import classNames from 'classnames';

import Field, { hasDataInFields } from '../../Field';
import BlockBuilder from '../../BlockBuilder';
import { ReviewRating } from '../../../../components';

import SectionContainer from '../SectionContainer';
import css from './SectionReviews.module.css';

const REVIEWS = [
  {
    rating: 5,
    review:
      'I saved over £800 on a 5-night stay at a luxury beach house that would have otherwise sat empty. The owner was happy, and I got an amazing deal!',
    userName: 'Jane Doe',
    userRole: 'Traveler',
  },
  {
    rating: 5,
    review:
      "pack2go has been a game-changer for my vacation property. I've filled what would have been empty nights and made an extra £4,200 this year alone.",
    userName: 'Michael Smith',
    userRole: 'Property Owner',
  },
  {
    rating: 5,
    review:
      'I set an alert for a beachfront property in Malibu and got notified about a 3-day gap at 50% off. My weekend getaway was incredible!',
    userName: 'Sarah Johnson',
    userRole: 'Traveler',
  },
];

const ReviewCard = ({ review, className }) => {
  const { rating, review: reviewText, userName, userRole } = review;

  const getInitials = name => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase();
  };

  return (
    <div className={className}>
      <div className={css.ratingWrapper}>
        <ReviewRating
          rating={rating}
          className={css.rating}
          reviewStarClassName={css.reviewRatingStar}
        />
      </div>
      {reviewText && <p className={css.reviewText}>"{reviewText}"</p>}
      <div className={css.userWrapper}>
        <div className={css.avatar}>{getInitials(userName)}</div>
        <div className={css.userInfo}>
          <span className={css.userName}>{userName}</span>
          <span className={css.userRole}>{userRole}</span>
        </div>
      </div>
    </div>
  );
};

// The number of columns (numColumns) affects styling and responsive images
const COLUMN_CONFIG = [
  { css: css.oneColumn, responsiveImageSizes: '(max-width: 767px) 100vw, 1200px' },
  { css: css.twoColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 600px' },
  { css: css.threeColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 400px' },
  { css: css.fourColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 265px' },
];
const getIndex = numColumns => numColumns - 1;
const getColumnCSS = numColumns => {
  const config = COLUMN_CONFIG[getIndex(numColumns)];
  return config ? config.css : COLUMN_CONFIG[0].css;
};
const getResponsiveImageSizes = numColumns => {
  const config = COLUMN_CONFIG[getIndex(numColumns)];
  return config ? config.responsiveImageSizes : COLUMN_CONFIG[0].responsiveImageSizes;
};

/**
 * @typedef {Object} BlockConfig
 * @property {string} blockId
 * @property {string} blockName
 * @property {'defaultBlock' | 'footerBlock' | 'socialMediaLink'} blockType
 */

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * Section component that's able to show blocks in multiple different columns (defined by "numColumns" prop)
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.defaultClasses
 * @param {string} props.defaultClasses.sectionDetails
 * @param {string} props.defaultClasses.title
 * @param {string} props.defaultClasses.description
 * @param {string} props.defaultClasses.ctaButton
 * @param {string} props.sectionId id of the section
 * @param {'columns'} props.sectionType
 * @param {Object?} props.title
 * @param {Object?} props.description
 * @param {Object?} props.appearance
 * @param {Object?} props.callToAction
 * @param {Array<BlockConfig>?} props.blocks array of block configs
 * @param {boolean?} props.isInsideContainer
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} Section for article content
 */
const SectionReviews = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    numColumns,
    title,
    description,
    appearance,
    callToAction,
    blocks = [],
    isInsideContainer = false,
    options,
  } = props;

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);
  const hasBlocks = true;

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      {hasHeaderFields ? (
        <header className={defaultClasses.sectionDetails}>
          <Field data={title} className={defaultClasses.title} options={fieldOptions} />
          <Field data={description} className={defaultClasses.description} options={fieldOptions} />
          <Field data={callToAction} className={defaultClasses.ctaButton} options={fieldOptions} />
        </header>
      ) : null}
      {hasBlocks ? (
        <div
          className={classNames(defaultClasses.blockContainer, getColumnCSS(numColumns), {
            [css.noSidePaddings]: isInsideContainer,
          })}
        >
          {REVIEWS.map((r, index) => (
            <ReviewCard className={css.reviewCard} key={index} review={r} />
          ))}
        </div>
      ) : null}
    </SectionContainer>
  );
};

export default SectionReviews;
